import { FormattedMessage } from "react-intl"
import classNames from "classnames"
import { Modal } from "../../../common"
import Avatar from "./Avatar"
import {
  getCurrentRole,
  getEducationEntries,
  getEmails,
  getExperienceEntries,
  getFitScore,
  getFullName,
  getInitials,
  getLinkedInUrl,
  getLocation,
  getPhones,
  getPictureUrl,
  getProfileId,
  getSkills,
  getWebsites,
  getYearsOfExperience,
} from "../utils"

/**
 * Read-only dossier shown in the existing platform Modal.
 *
 * Reuses common/Modal (same component the delete dialog and labels modal
 * use) — the sourcing-specific sections (Contact / Experience / Education /
 * Skills) are laid out inside.
 */
const CandidateDetailModal = ({ open, setOpen, profile, selected, onToggle, options }) => {
  if (!profile) return null

  const id = getProfileId(profile)
  const fullName = getFullName(profile)
  const initials = getInitials(profile)
  const pictureUrl = getPictureUrl(profile)
  const { title, company } = getCurrentRole(profile)
  const location = getLocation(profile)
  const fit = getFitScore(profile)
  const skills = getSkills(profile, 30)
  const emails = getEmails(profile)
  const phones = getPhones(profile)
  const websites = getWebsites(profile)
  const linkedin = getLinkedInUrl(profile)
  const yearsExp = getYearsOfExperience(profile)
  const experiences = getExperienceEntries(profile, 6)
  const educations = getEducationEntries(profile, 6)

  // Raw profile payload for the `has_*` hidden-data flags. Pearch sets these
  // to true when it *knows* a profile has emails/phones even if the arrays
  // come back empty (reveal toggle was off for the search).
  const rawProfile = profile?.profile || profile || {}
  const hasHiddenEmails = rawProfile.has_emails === true && emails.length === 0
  const hasHiddenPhones = rawProfile.has_phone_numbers === true && phones.length === 0

  const hasContact =
    emails.length > 0 ||
    phones.length > 0 ||
    websites.length > 0 ||
    linkedin ||
    hasHiddenEmails ||
    hasHiddenPhones

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      closeButton
      closeOnOutsideClick
      className="sourcing-dossier"
    >
      <div className="sourcing-dossier__head">
        <Avatar
          url={pictureUrl}
          initials={initials}
          size="large"
          className="sourcing-dossier__avatar"
        />
        <div className="sourcing-dossier__head-info">
          <h2 className="sourcing-dossier__name">{fullName}</h2>
          {title && (
            <p className="sourcing-dossier__role">
              {title}
              {company && (
                <>
                  {" at "}
                  <span className="sourcing-dossier__company">{company}</span>
                </>
              )}
            </p>
          )}
          {location && (
            <p className="sourcing-dossier__loc">{location}</p>
          )}
        </div>
      </div>

      {fit !== null && (
        <div className="sourcing-dossier__fit">
          <div className="sourcing-dossier__fit-label">
            <FormattedMessage id="sourcing.fitScore" />
          </div>
          <div className="sourcing-dossier__fit-num">{fit}</div>
        </div>
      )}

      {hasContact && (
        <div className="sourcing-dossier__section">
          <h4>
            <FormattedMessage id="sourcing.dossier.contact" />
          </h4>
          <div className="sourcing-dossier__pills">
            {linkedin && (
              <a
                className="sourcing-dossier__pill"
                href={linkedin}
                target="_blank"
                rel="noreferrer"
              >
                in/{linkedin.split("/").filter(Boolean).pop()}
              </a>
            )}
            {emails.map((e) => (
              <a
                key={e}
                className="sourcing-dossier__pill"
                href={`mailto:${e}`}
              >
                {e}
              </a>
            ))}
            {phones.map(({ number, type }) => (
              <a
                key={number}
                className="sourcing-dossier__pill"
                href={`tel:${number.replace(/\s+/g, "")}`}
              >
                {number}
                {type && (
                  <span className="sourcing-dossier__pill-tag">{type}</span>
                )}
              </a>
            ))}
            {websites.map((w) => (
              <a
                key={w}
                className="sourcing-dossier__pill"
                href={w}
                target="_blank"
                rel="noreferrer"
              >
                {prettyHost(w)}
              </a>
            ))}
          </div>

          {/*
            Hidden-data hints. Pearch only returns emails/phones when the
            corresponding Reveal toggle is on at search time. If the toggle was
            off (options.reveal_emails / reveal_phones === false), show a short
            note so the recruiter knows the data exists and can re-run the
            search to surface it.
          */}
          {(hasHiddenEmails || hasHiddenPhones) && (
            <p className="sourcing-dossier__contact-hint">
              {hasHiddenEmails && !options?.reveal_emails && (
                <FormattedMessage id="sourcing.dossier.emailsHidden" />
              )}
              {hasHiddenEmails && hasHiddenPhones && !options?.reveal_emails && !options?.reveal_phones && (
                <br />
              )}
              {hasHiddenPhones && !options?.reveal_phones && (
                <FormattedMessage id="sourcing.dossier.phonesHidden" />
              )}
            </p>
          )}
        </div>
      )}

      {experiences.length > 0 && (
        <div className="sourcing-dossier__section">
          <h4>
            <FormattedMessage id="sourcing.dossier.experience" />
            {yearsExp != null && (
              <span className="sourcing-dossier__hint">
                <FormattedMessage
                  id="sourcing.dossier.yearsOfExperience"
                  values={{ years: yearsExp }}
                />
              </span>
            )}
          </h4>
          <div className="sourcing-dossier__timeline">
            {experiences.map((exp, i) => {
              const start = exp.start_date ? String(exp.start_date).slice(0, 4) : ""
              const rawEnd = exp.end_date || "current"
              const end = typeof rawEnd === "string" ? (rawEnd.slice(0, 4) || rawEnd) : rawEnd
              return (
                <div key={`${exp.title}-${exp.company}-${i}`} className="sourcing-dossier__exp">
                  <div className="sourcing-dossier__exp-date">
                    {start && <>{start} — </>}
                    <span className={classNames({ "sourcing-dossier__exp-now": end === "current" })}>
                      {end}
                    </span>
                  </div>
                  <div className="sourcing-dossier__exp-role">
                    <p className="sourcing-dossier__exp-title">{exp.title}</p>
                    {exp.company && <p className="sourcing-dossier__exp-co">{exp.company}</p>}
                    {exp.description && <p className="sourcing-dossier__exp-desc">{exp.description}</p>}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {educations.length > 0 && (
        <div className="sourcing-dossier__section">
          <h4>
            <FormattedMessage id="sourcing.dossier.education" />
          </h4>
          <div className="sourcing-dossier__timeline">
            {educations.map((edu, i) => {
              const start = edu.start_date ? String(edu.start_date).slice(0, 4) : ""
              const end = edu.end_date ? String(edu.end_date).slice(0, 4) : ""
              // Prefer the mapped degree label ("Master's"); fall back to the
              // raw `major` string (which frequently already reads like a
              // full title, e.g. "MS, Computer Science").
              const heading = edu.degree || edu.major || "Education"
              const showMajorBelow =
                edu.major && edu.major !== edu.degree && edu.major !== heading
              return (
                <div key={`${edu.campus}-${i}`} className="sourcing-dossier__exp">
                  <div className="sourcing-dossier__exp-date">
                    {start && <>{start} — </>}
                    <span>{end || ""}</span>
                  </div>
                  <div className="sourcing-dossier__exp-role">
                    <p className="sourcing-dossier__exp-title">{heading}</p>
                    {edu.campus && <p className="sourcing-dossier__exp-co">{edu.campus}</p>}
                    {showMajorBelow && (
                      <p className="sourcing-dossier__exp-desc">{edu.major}</p>
                    )}
                    {edu.specialization && (
                      <p className="sourcing-dossier__exp-desc">{edu.specialization}</p>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {skills.length > 0 && (
        <div className="sourcing-dossier__section">
          <h4>
            <FormattedMessage id="sourcing.dossier.skills" />
          </h4>
          <div className="sourcing-dossier__skills">
            {skills.map((s) => (
              <span key={s} className="sourcing-dossier__skill">{s}</span>
            ))}
          </div>
        </div>
      )}

      <div className="sourcing-dossier__foot">
        <button
          type="button"
          className="btn btn--small"
          onClick={() => setOpen(false)}
        >
          <FormattedMessage id="sourcing.dossier.close" />
        </button>
        <button
          type="button"
          className={classNames("btn primary btn--small", {
            disabled: selected,
          })}
          onClick={() => {
            onToggle(id, !selected)
            setOpen(false)
          }}
        >
          <FormattedMessage
            id={selected ? "sourcing.dossier.alreadySelected" : "sourcing.dossier.addToSelection"}
          />
        </button>
      </div>
    </Modal>
  )
}

// Strip protocol + trailing slash for a more readable link label.
const prettyHost = (url) => {
  try {
    const u = new URL(url)
    return u.hostname.replace(/^www\./, "") + (u.pathname === "/" ? "" : u.pathname)
  } catch {
    return url
  }
}

export default CandidateDetailModal
